import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'

import type { PollScore } from '@/shared/tools/communicationBoardPolls'
import type { BoardPollsState, PollDraft, PollVoteFailure } from './types'

const initialState: BoardPollsState = {
  drafts: {},
  votes: {},
  loadedKeys: [],
  sendingKeys: [],
  failedKeys: [],
  closedKeys: [],
}

const EMPTY_DRAFT: PollDraft = { score: null, comment: '' }

const without = (keys: string[], key: string) => keys.filter(current => current !== key)

const draftOf = (state: BoardPollsState, pollKey: string) => {
  state.drafts[pollKey] ??= { ...EMPTY_DRAFT }
  return state.drafts[pollKey]
}

const votesLoadedHandler = (
  state: BoardPollsState,
  { payload }: PayloadAction<{ pollKeys: string[]; votes: Record<string, PollScore> }>,
) => {
  Object.assign(state.votes, payload.votes)
  state.loadedKeys = [...new Set([...state.loadedKeys, ...payload.pollKeys])]
}

const scoreSelectedHandler = (
  state: BoardPollsState,
  { payload }: PayloadAction<{ pollKey: string; score: PollScore }>,
) => {
  draftOf(state, payload.pollKey).score = payload.score
  state.failedKeys = without(state.failedKeys, payload.pollKey)
}

const commentChangedHandler = (
  state: BoardPollsState,
  { payload }: PayloadAction<{ pollKey: string; comment: string }>,
) => {
  draftOf(state, payload.pollKey).comment = payload.comment
}

const voteRequestedHandler = (state: BoardPollsState, { payload }: PayloadAction<string>) => {
  state.sendingKeys = [...without(state.sendingKeys, payload), payload]
  state.failedKeys = without(state.failedKeys, payload)
}

const voteSucceededHandler = (
  state: BoardPollsState,
  { payload }: PayloadAction<{ pollKey: string; score: PollScore }>,
) => {
  state.votes[payload.pollKey] = payload.score
  state.sendingKeys = without(state.sendingKeys, payload.pollKey)
  delete state.drafts[payload.pollKey]
}

const voteFailedHandler = (
  state: BoardPollsState,
  { payload }: PayloadAction<{ pollKey: string; score: PollScore; failure: PollVoteFailure }>,
) => {
  state.sendingKeys = without(state.sendingKeys, payload.pollKey)

  if (payload.failure === 'already_voted') {
    state.votes[payload.pollKey] = payload.score
    delete state.drafts[payload.pollKey]
  } else if (payload.failure === 'closed') {
    state.closedKeys = [...without(state.closedKeys, payload.pollKey), payload.pollKey]
  } else {
    state.failedKeys = [...without(state.failedKeys, payload.pollKey), payload.pollKey]
  }
}

const slice = createSlice({
  name: 'communicationBoardPolls',
  initialState,
  reducers: {
    votesLoaded: votesLoadedHandler,
    scoreSelected: scoreSelectedHandler,
    commentChanged: commentChangedHandler,
    voteRequested: voteRequestedHandler,
    voteSucceeded: voteSucceededHandler,
    voteFailed: voteFailedHandler,
  },
})

export const { votesLoaded, scoreSelected, commentChanged, voteRequested, voteSucceeded, voteFailed } = slice.actions

export default slice.reducer
